import { getServiceWorkerRegistration } from '../serviceWorkerRegistration';
import { getVapidPublicKey, subscribePush, unsubscribePush } from '../redux/ducks/push';

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
    return out;
}

export function isPushSupported() {
    if (typeof window === 'undefined') return false;
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function getPermission() {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
}

let vapidPublicKeyPromise = null;

export function fetchVapidPublicKey(dispatch) {
    if (!vapidPublicKeyPromise) {
        vapidPublicKeyPromise = new Promise((resolve) => dispatch(getVapidPublicKey(resolve)))
            .then((resp) => {
                if (!resp?.ok) {
                    vapidPublicKeyPromise = null;
                    return null;
                }
                if (!resp.data?.enabled || !resp.data?.publicKey) return null;
                return resp.data.publicKey;
            })
            .catch((err) => {
                vapidPublicKeyPromise = null;
                throw err;
            });
    }
    return vapidPublicKeyPromise;
}

export async function subscribeToPush(dispatch) {
    if (!isPushSupported()) throw new Error('Push notifications are not supported in this browser');

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') throw new Error('Notification permission denied');

    const reg = await getServiceWorkerRegistration();
    if (!reg) throw new Error('Service worker not ready');

    const publicKey = await fetchVapidPublicKey(dispatch);
    if (!publicKey) throw new Error('Push notifications are not configured on the server');

    let subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
        subscription = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
    }

    const json = subscription.toJSON();
    const resp = await new Promise((resolve) => dispatch(subscribePush({
        endpoint: json.endpoint,
        expirationTime: json.expirationTime || null,
        keys: json.keys,
        userAgent: navigator.userAgent,
    }, resolve)));
    if (!resp?.ok) throw new Error(resp?.message || 'Failed to register subscription');

    return subscription;
}

export async function unsubscribeFromPush(dispatch) {
    const reg = await getServiceWorkerRegistration();
    if (!reg) return;
    const subscription = await reg.pushManager.getSubscription();
    if (!subscription) return;

    const endpoint = subscription.endpoint;
    await subscription.unsubscribe().catch(() => null);
    await new Promise((resolve) => dispatch(unsubscribePush(endpoint, resolve)));
}

export async function getCurrentSubscription() {
    const reg = await getServiceWorkerRegistration();
    if (!reg) return null;
    return reg.pushManager.getSubscription();
}
